import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {User} from '../../../shared/models/user.model';
import {AuthService} from "../../../shared/services/auth.service";
import {SharedApiService} from "../../../shared/services/api.service";
import {TranslateService} from "@ngx-translate/core";
import {ToastrService} from "ngx-toastr";
import {Article} from "../../models/article.model";
import {AddToShoppingCart} from "../../../shared/models/add-to-shopping-cart.model";
import {ShoppingCartCountService} from "../../../shared/services/shopping_cart_count";
import {AlertService} from "../../../shared/services/alert.service";
import {Video} from "../../models/video.model";
import {VideoModalComponent} from "../../../shared/components/video-modal/video-modal.component";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {DomSanitizer} from "@angular/platform-browser";
import {ArticleAttribute, ArticleAttributes} from "../../models/articleAttribute.model";
import {ArticleAttributeType} from "../../models/articleAttributeType.model";
import {ArticleStock} from "../../../shared/models/article-stock.model";
import {ArticleStockByLocation} from "../../../shared/models/article-stock-by-location.model";
import {ArticleStockByLocationService} from "../../../shared/services/article-stock-by-location.service";
import {ArticlePriceService} from "../../../shared/services/article-price.service";
import {ViewportScroller} from '@angular/common';
import {LoginModalComponent} from "../login-modal/login-modal.component";

@Component(
        {
            templateUrl: './article.component.html',
        }
)
export class ArticleComponent implements OnInit {
    @ViewChild('articleDescription') public articleDescription: ElementRef;
    @ViewChild('sectionInformation') public sectionInformation!: ElementRef;
    @ViewChild('sectionDocuments') public sectionDocuments!: ElementRef;
    @ViewChild('sectionVideos') public sectionVideos!: ElementRef;
    @ViewChild('sectionFaq') public sectionFaq!: ElementRef;

    public article: Article;
    public locationGuid: string = null;
    public showStockByLocation: boolean = false;
    public stock: ArticleStock;
    public stockByLocation: ArticleStockByLocation;
    public hasVolumePrices: boolean = false;
    public volumePrices: Array<any> = [];
    public user?: User;
    public canOrder: boolean;
    public showCompleteDescription: boolean = false;
    public showReadMoreOrLessButton: boolean = false;
    public attributes: ArticleAttributes = [];
    public discountPercentage: number;

    constructor(
            public authService: AuthService,
            private route: ActivatedRoute,
            private router: Router,
            private apiService: SharedApiService,
            private translateService: TranslateService,
            private toastrService: ToastrService,
            private shoppingCartCountService: ShoppingCartCountService,
            private alertService: AlertService,
            private modalService: NgbModal,
            private sanitizer: DomSanitizer,
            private articleStockByLocationService: ArticleStockByLocationService,
            private articlePriceService: ArticlePriceService,
            private viewportScroller: ViewportScroller
    ) {
    }

    public ngOnInit() {
        this.route.data.subscribe(data => {
            if (data.hasOwnProperty('articleResult')) {
                this.article = data.articleResult;

                if (!this.article.quantity || this.article.quantity === 0) {
                    let quantityMultiplier = this.article.quantityMultiplier;
                    if (!quantityMultiplier) {
                        quantityMultiplier = 1;
                    }

                    this.article.quantity = quantityMultiplier;
                }

                this.attributes = [
                    {
                        guid: this.article.guid,
                        type: {
                            guid: this.article.guid,
                            cid: 'articleNumber',
                            name: 'Artikelnummer',
                        } as ArticleAttributeType,
                        value: this.article.cid
                    } as ArticleAttribute,
                    ...this.article.articleAttributes
                ];
            }
        });

        this.canOrder = this.authService.hasRoleOrderer();

        if (this.canOrder) {
            this.articlePriceService
                    .subscribeToArticle(this.article.guid)
                    .subscribe(price => {
                        this.hasVolumePrices = price.hasVolumePrices;
                        this.volumePrices = price.volumePrices;
                        this.discountPercentage = price.discountPercentage;

                        if (price.hasOwnProperty('stock')) {
                            this.stock = {
                                articleGuid: price.articleGuid,
                                stock: price.stock
                            } as ArticleStock;
                        }
                    });

            this.apiService.getAssortmentArticles().subscribe(articles => {
                if (articles.find(article => article.guid === this.article.guid)) {
                    this.article.inAssortment = true;
                }
            });
        }
    }

    public ngAfterViewInit() {
        if (this.articleDescription.nativeElement.offsetHeight < this.articleDescription.nativeElement.scrollHeight) {
            this.showReadMoreOrLessButton = true;
        }
    }

    public stockClass(): string {
        if (this.showStockByLocation && this.locationGuid) {
            if (this.stockByLocation && 'stock' in this.stockByLocation && this.locationGuid in this.stockByLocation.stock) {
                const stock = this.stockByLocation.stock[this.locationGuid].stock;
                if (stock <= 0) {
                    return 'out';
                }

                return 'in';
            }

            return 'unknown';
        }

        if (!this.stock || this.stock.stock === null || this.stock.stock === undefined) {
            return 'unknown';
        }

        if (this.stock.stock <= 0) {
            return 'out';
        }

        return 'in';
    }

    public loadStockForMultipleLocations() {
        if (!this.stockByLocation) {
            this.articleStockByLocationService
                    .subscribeToArticle(this.article.guid)
                    .subscribe(stock => {
                        this.stockByLocation = stock;
                    });
        }
    }

    public setQuantity(quantity?: number) {
        this.article.quantity = quantity;
    }

    public changeQuantity(quantity) {
        quantity = parseInt(quantity);

        if (isNaN(quantity) || quantity === 0) {
            this.setQuantity(null);

            return;
        }

        this.setQuantity(quantity);
    }

    public addToShoppingCart() {
        let article = null;

        if (this.article.quantity) {
            article = (
                    {
                        guid: this.article.guid,
                        quantity: this.article.quantity,
                    } as AddToShoppingCart
            );
        }

        if (!article) {
            this.alertService.alert(this.translateService.instant('global.notification.set_quantity'));
        } else {
            this.apiService.addToShoppingCart([article]).subscribe(result => {
                if ((result.length || 0) > 0) {
                    this.setQuantity(null);

                    this.shoppingCartCountService.updateCount();
                    this.toastrService.success(this.translateService.instant('page.assortment.added_to_shopping_cart'));
                }
            });
        }
    }

    public addToFavorites() {
        this.article.inAssortment = true;

        this.apiService.addToAssortment([this.article.guid]).subscribe(() => {
            this.toastrService.success(this.translateService.instant('page.assortment.added_to_assortment'));
        });
    }

    public removeFromFavorites() {
        this.article.inAssortment = false;

        this.apiService.removeFromAssortment(this.article.guid).subscribe(() => {
            this.toastrService.success(this.translateService.instant('page.assortment.removed'));
        });
    }

    /**
     * Scroll to element
     */
    public scrollToInformation() {
        this.sectionInformation.nativeElement.scrollIntoView({behavior: 'smooth', block: 'center'});
    }

    public scrollToDocuments() {
        this.sectionDocuments.nativeElement.scrollIntoView({behavior: 'smooth', block: 'center'});
    }

    public scrollToVideos() {
        this.sectionVideos.nativeElement.scrollIntoView({behavior: 'smooth', block: 'center'});
    }

    public scrollToFaq() {
        this.sectionFaq.nativeElement.scrollIntoView({behavior: 'smooth', block: 'center'});
    }

    public openVideoModal(video: Video) {
        const modalRef = this.modalService.open(VideoModalComponent, {size: 'full'});

        modalRef.componentInstance.videoUrl = this.sanitizer.bypassSecurityTrustResourceUrl('https://www.youtube.com/embed/' + video.youtubeIdentifier + '?rel=0&modestbranding=1&autoplay=1');
    }

    public onClick(elementId: string): void {
        this.viewportScroller.scrollToAnchor(elementId);
    }

    public openLoginModal() {
        const modalRef = this.modalService.open(LoginModalComponent);

        modalRef.componentInstance.currentRoute = this.router.url;
    }

    public getQueryParams(level: number) {
        let queryParams = {};

        for (let i = 1; i <= level; i++) {
            queryParams['filters[product_hierarchy_level_' + i + '][]'] = this.article.product.hierarchies[i - 1].name;
        }

        return queryParams;
    }
}
